import { useEffect, useState, useCallback } from "react";
import {
  getGlobalCalendar,
  createGlobalOverride,
  deleteGlobalOverride,
} from "./adminCalendarService";
import "./calendar.css";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const fmtMonth = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const buildGrid = (year, month) => {
  const first = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = Array(first).fill(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const AdminGlobalCalendarPage = () => {
  const today = new Date();
  const [cur, setCur] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getGlobalCalendar(fmtMonth(cur));
      setOverrides(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  }, [cur]);

  useEffect(() => { load(); }, [load]);

  const overrideMap = Object.fromEntries(overrides.map((o) => [o.date, o]));

  const handleClick = async (day) => {
    if (!day) return;
    const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const existing = overrideMap[dateStr];
    try {
      if (existing) {
        await deleteGlobalOverride(existing.id || existing.overrideId);
      } else {
        const reason = window.prompt(`Override ${dateStr} — enter reason (optional):`, "") ?? "";
        if (reason === null) return;
        // default toggle: if no existing override, mark as HOLIDAY; if already STUDY_DAY override, toggle
        await createGlobalOverride({ date: dateStr, dayType: "HOLIDAY", reason });
      }
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Action failed.");
    }
  };

  const handleRightClick = async (e, day) => {
    e.preventDefault();
    if (!day) return;
    const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const existing = overrideMap[dateStr];
    if (!existing) {
      // create STUDY_DAY override
      const reason = window.prompt(`Mark ${dateStr} as STUDY_DAY — reason (optional):`, "") ?? "";
      if (reason === null) return;
      try {
        await createGlobalOverride({ date: dateStr, dayType: "STUDY_DAY", reason });
        await load();
      } catch (err) {
        alert(err?.response?.data?.message || "Action failed.");
      }
      return;
    }
    // toggle existing override type
    try {
      await deleteGlobalOverride(existing.id || existing.overrideId);
      await createGlobalOverride({
        date: dateStr,
        dayType: existing.dayType === "STUDY_DAY" ? "HOLIDAY" : "STUDY_DAY",
        reason: existing.reason || "",
      });
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Action failed.");
    }
  };

  const cells = buildGrid(cur.getFullYear(), cur.getMonth());
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  return (
    <div>
      <div className="ac-header">
        <h2>Global Calendar</h2>
        <p style={{ margin: 0, fontSize: "0.82rem", color: "#6b7280" }}>
          Left-click a day to mark HOLIDAY · Right-click to mark STUDY_DAY · Click an overridden day to remove override
        </p>
      </div>

      <div className="cal-wrap">
        <div className="cal-nav">
          <button className="cal-nav-btn" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() - 1, 1))}>‹</button>
          <h3>{cur.toLocaleString("default", { month: "long", year: "numeric" })}</h3>
          <button className="cal-nav-btn" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() + 1, 1))}>›</button>
        </div>

        <div className="cal-dow-row">
          {DOW.map((d) => <div key={d} className="cal-dow-cell">{d}</div>)}
        </div>

        {loading ? (
          <div className="ac-loading">Loading…</div>
        ) : (
          <div className="cal-grid">
            {cells.map((day, i) => {
              if (!day) return <div key={i} className="cal-cell cal-empty" />;
              const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const ov = overrideMap[dateStr];
              const cls = ov ? (ov.dayType === "STUDY_DAY" ? "cal-study" : "cal-holiday") : "";
              const isToday = dateStr === todayStr;
              return (
                <div
                  key={i}
                  className={`cal-cell ${cls} ${isToday ? "cal-today" : ""}`}
                  onClick={() => handleClick(day)}
                  onContextMenu={(e) => handleRightClick(e, day)}
                  title={ov ? `${ov.dayType} (override)${ov.reason ? ` — ${ov.reason}` : ""}` : dateStr}
                >
                  <div className="cal-day-num">{day}</div>
                  {ov && (
                    <>
                      <span className={`cal-dot cal-dot-${ov.dayType === "STUDY_DAY" ? "study" : "holiday"}`} />
                      <span style={{ fontSize: "0.65rem", color: ov.dayType === "STUDY_DAY" ? "#15803d" : "#b91c1c" }}>
                        {ov.dayType === "STUDY_DAY" ? "Study" : "Holiday"}
                      </span>
                      <div style={{ fontSize: "0.6rem", color: "#9ca3af", marginTop: 2 }}>override</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginTop: "0.75rem", display: "flex", gap: "1rem", fontSize: "0.8rem", color: "#6b7280" }}>
        <span><span className="cal-dot cal-dot-study" /> Study Day override</span>
        <span><span className="cal-dot cal-dot-holiday" /> Holiday override</span>
        <span style={{ color: "#9ca3af" }}>Unmarked = default from settings</span>
      </div>
    </div>
  );
};

export default AdminGlobalCalendarPage;
